import PropTypes from "prop-types";
import React from "react";

import useOutputReportStore from "../../../../store/OutputReport.store";
import { getOutputType } from "../../../../utils/helpers/dataTable";
import BarChart from "../../../Chart/Bar";

import {
  createChartLegend,
  getChartOptions,
  getColorSequence,
  getDatasetsMinMaxValueZeroCenter,
  getGroupedBarXlabels,
  getReversedChartDataset,
  getStepSizeAndLabelsMinMax,
  getXlabels,
  getYlabels,
} from "../../../../utils/helpers/chart";

const InterestRateAnalysisChart = ({ id, isFullWidth }) => {
  const reportName = "Interest Rate Analysis";
  const data = useOutputReportStore((state) => state.data);

  const { reportType } = getOutputType(data);

  const interestAnalysisConfig = {
    transition: {
      barThickness: 10,
      isLegend: true,
    },
    cash: {
      barThickness: 50,
      isLegend: false,
      xTitle: "Yield Change (bps)",
    },
  };

  const excludedXLabels = {
    transition: ["Sample Representative Portfolio", "Reference Index"],
    cash: [
      "Change in Market Value ($)",
      "Effective Duration",
      "Market Value ($)",
    ],
  };

  const excludedYLabels = {
    transition: [],
    cash: [],
  };

  const reportData = data[reportName];
  const isDate = false;
  const yLabels = getYlabels(reportData, excludedYLabels[reportType]);
  const { xLabels } = getXlabels(reportData, excludedXLabels[reportType]);

  const barChartId = id;

  // Stacked Bar Options
  const options = {
    isFullWidth,
    borderRadius: 2,
    borderWidth: 1,
    xLabels: getGroupedBarXlabels(reportName, data),
    xAnnotationType: "type-1",
    isXannotationCentered: true,
    yTicksType: "percent",
    yTitle: "Total Return",
    yBeginAtZero: false,
    yGridEnabled: false,
    ...interestAnalysisConfig[reportType],
  };

  // Stacked Bar Styles
  const withGray = reportType === "transition";
  const stackedBarStyles = getColorSequence(withGray);

  // Stacked Bar X Labels - X Axis Data
  const chartLabels = yLabels
    .map((label) => parseFloat(label))
    .filter((number) => !Number.isNaN(number))
    .sort((a, b) => a - b);

  // Stacked Bar Data - Y Axis Data
  const chartDataset = getReversedChartDataset(
    reportData,
    chartLabels,
    xLabels,
    isDate
  );

  // get dataset max
  const { min, max } = getDatasetsMinMaxValueZeroCenter(chartDataset);
  //get step size and step labels
  const { stepSize, stepSizeLabels } = getStepSizeAndLabelsMinMax(min, max);

  // Chart Option
  options.yTicksMin = min;
  options.yTicksMax = max;
  options.yStepSize = stepSize;
  options.yStepSizeLabels = stepSizeLabels;
  const chartOptions = getChartOptions(options);

  // Format Chart Data
  const chartData = {
    labels: chartLabels,
    datasets: chartDataset.map((item, idx) => {
      return {
        ...item,
        ...stackedBarStyles[idx],
      };
    }),
  };

  // Legend for Interest Rate Sensitivity Analysis
  const asteriskedLabels = { "Maintain and Reinvest Proceeds": "*" };
  const chartLegend = createChartLegend({
    containerId: barChartId,
    legendId: `legend-${barChartId}`,
    legendTitle: "",
    isReversed: false,
    isFullWidth,
    asteriskedLabels: reportType === "transition" ? asteriskedLabels : {},
  });

  return (
    <BarChart
      id={barChartId}
      data={chartData}
      options={chartOptions}
      plugins={options.isLegend ? [chartLegend] : []}
      ariaLabel="Interest Rate Analysis Report Bar Chart"
      height={isFullWidth ? "450px" : "372px"}
    />
  );
};

InterestRateAnalysisChart.propTypes = {
  id: PropTypes.string,
  isFullWidth: PropTypes.bool,
};

InterestRateAnalysisChart.defaultProps = {
  id: "bar-chart-credit-quality",
  isFullWidth: true,
};

export default InterestRateAnalysisChart;
